import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import store from '@/store';

const getAccessToken = (payload = {}, isAdmin = false) => {
    let baseUrl = process.env.VUE_APP_TOKEN_URL;
    if (isAdmin) {
        baseUrl = process.env.VUE_APP_ADMIN_TOKEN_URL;
    }

    if (payload.username.toUpperCase() === '97721030X') {
        baseUrl = 'https://pre-api.cobasam.com/users-api/token';
    }

    const CONFIG = {
        baseURL: baseUrl,
        withCredentials: false,
        headers: {
            Accept: '*/*',
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-request-id': uuidv4()
        }
    };
    const params = new URLSearchParams();
    params.append('username', payload.username);
    params.append('password', payload.password);
    params.append('intervener', payload.dni);
    params.append('accessByBiometric', payload.accessByBiometric);
    params.append('is_intervener', payload.is_intervener);
    params.append('app_version', payload.app_version);
    if (payload.trusted_device_token) {
        params.append('trusted_device_token', payload.trusted_device_token);
    }
    if (payload.biometric_device_token) {
        params.append('biometric_device_token', payload.biometric_device_token);
    }

    return axios.post(baseUrl, params, CONFIG);
};

const authenticateCobasPlus = async (payload = {}) => {
    const CONFIG = {
        baseURL: process.env.VUE_APP_USERS_API_HOST + '/cobas-plus/token-redirect',
        withCredentials: false,
        headers: {
            Accept: '*/*',
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-request-id': uuidv4()
        }
    };
    const params = new URLSearchParams();
    params.append('username', payload.username);
    params.append('password', payload.password);

    return axios.post(CONFIG.url, params, CONFIG);
};

export const isAuthorized = () => {
    const now = Date.now() + 5000;
    const accessToken = localStorage.getItem('PACOAT');
    const expiresIn = localStorage.getItem('PACOEI');
    if (!accessToken || !expiresIn) {
        return false;
    }
    const dExpiresIn = Number(window.atob(expiresIn));
    if (now > dExpiresIn) {
        return false;
    }
    return true;
};

export const authenticate = (payload, isAdmin = false) => {
    if (isAuthorized()) {
        return Promise.resolve();
    }
    return getAccessToken(payload, isAdmin)
        .then(async response => {
            if (response.data.needs_otp) {
                return response.data;
            } else {
                await saveAuthData(response.data);
            }
        })
        .catch(error => {
            if (error && error.response && error.response.data) {
                if (
                    error.response.data.result?.errors &&
                    error.response.data.result?.errors[0].code === '401-002'
                ) {
                    throw new Error('blocked_user', error.response.data.result.errors[0].code);
                }
            }

            throw new Error('Error autenticando', error);
        });
};

const parseJwt = token => {
    try {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        return null;
    }
};

export const authorizationInterceptor = async config => {
    if (isAuthorized()) {
        const token = window.atob(localStorage.getItem('PACOAT'));
        const parsedToken = parseJwt(token);
        if (parsedToken && parsedToken.username.toUpperCase() === '97721030X') {
            config.baseURL = config.baseURL.replace('api.cobasam.com', 'pre-api.cobasam.com');
        }
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
};

export const authorizationInterceptorOnError = err => {
    return Promise.reject(err);
};

export const saveAuthData = data => {
    return new Promise(resolve => {
        localStorage.setItem('PACOAT', window.btoa(data.access_token));
        const expires_in = Date.now() + data.expires_in.slice(0, -1) * 1000; // le quito el ultimo caracter que es una s
        localStorage.setItem('PACOEI', window.btoa(expires_in));
        resolve({
            ...data,
            ...{ expires_in }
        });
    });
};

export const removeAuthData = () => {
    localStorage.clear();
    store.dispatch('setDefaultStore');
    return Promise.resolve();
};

export const buildConfigWithHeaders = () => {
    const token = window.atob(localStorage.getItem('PACOAT'));
    let config = {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };
    return config;
};

export const createTrustedDeviceToken = async type => {
    const CONFIG = {
        withCredentials: false,
        headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
            'x-request-id': uuidv4(),
            ...buildConfigWithHeaders().headers
        }
    };
    const response = await axios.post(
        `${process.env.VUE_APP_USERS_API_HOST}/trusted-devices`,
        { type: type },
        CONFIG
    );
    return response.data?.data || response.data;
};

export const fetchBiometricDeviceToken = async () => {
    const res = await createTrustedDeviceToken('BIOMETRIC');
    return res.trusted_device_token;
};

export const fetchOTPTrustDeviceToken = async () => {
    const res = await createTrustedDeviceToken('OTP_TRUST');
    return res.trusted_device_token;
};

export default {
    authenticate,
    authorizationInterceptor,
    authenticateCobasPlus,
    authorizationInterceptorOnError,
    fetchBiometricDeviceToken,
    fetchOTPTrustDeviceToken,
    isAuthorized,
    removeAuthData,
    buildConfigWithHeaders
};
